/**
 * First-run setup (audit #10, UX-A8). Sign-up creates the company's legal entity, a head office and
 * the owner's employment; the steps here are the rest. Every step's "done" is computed from the org's
 * own data, so it is done the moment the thing exists, however it was created. The only things
 * stored are the owner's first-run choices (`setupPreferencesSchema`, in Organization.settings).
 *
 * Labels and links live in the web app; the contract carries keys only.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** In the order the checklist shows them — each one tends to unblock the next. */
export const SETUP_STEP_KEYS = [
  'legal_entity',
  // Sign-up creates the legal entity with a detected country; this is the owner confirming it and
  // adding the registered address.
  'company_details',
  'locations',
  'departments',
  'grades',
  'designations',
  'employee_id_series',
  'probation_policy',
  'holidays',
  'shifts',
  'owner_employment',
  'employees',
  'team_signed_in',
  // Someone other than the owner has clocked in (any attendance punch, from the phone app or the
  // web). Only applies when the Attendance module is on the plan.
  'first_punch',
] as const;
export type SetupStepKey = (typeof SETUP_STEP_KEYS)[number];

/**
 * The areas an owner can say they want to set up first, on the welcome screen. They only decide the
 * ORDER of what Home and the checklist suggest; they never switch a module on or off.
 */
export const SETUP_FOCUS_AREAS = [
  'attendance',
  'leave',
  'payroll',
  'hiring',
  'expenses',
  'performance',
] as const;
export type SetupFocusArea = (typeof SETUP_FOCUS_AREAS)[number];

/**
 * What the organisation told the first-run flow, stored per company (not per browser, so hiding the
 * checklist on one laptop does not hide it for another company on the same laptop).
 */
export const setupPreferencesSchema = z.object({
  /** When the owner finished or skipped the welcome setup. Null until then. */
  welcomeCompletedAt: z.string().datetime().nullable(),
  /** In the order picked; empty when nothing was picked. */
  focusAreas: z.array(z.enum(SETUP_FOCUS_AREAS)),
  /** "Hide for now" on the Getting started panel: hidden until this instant. Null when shown. */
  checklistHiddenUntil: z.string().datetime().nullable(),
});
export type SetupPreferences = z.infer<typeof setupPreferencesSchema>;

export const updateSetupPreferencesBodySchema = z
  .object({
    /** Marks the welcome setup as finished (or skipped). There is no way to un-finish it. */
    welcomeCompleted: z.literal(true).optional(),
    focusAreas: z
      .array(z.enum(SETUP_FOCUS_AREAS))
      .max(SETUP_FOCUS_AREAS.length)
      .refine((areas) => new Set(areas).size === areas.length, 'Each area can be picked once.')
      .optional(),
    /** Hide the Getting started panel for this many days; 0 shows it again. */
    hideChecklistForDays: z.number().int().min(0).max(30).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, 'Nothing to update.');
export type UpdateSetupPreferencesBody = z.infer<typeof updateSetupPreferencesBodySchema>;

export const setupStatusSchema = z.object({
  steps: z.array(
    z.object({
      key: z.enum(SETUP_STEP_KEYS),
      done: z.boolean(),
      /**
       * False when the step's module is not part of this organisation's plan (holidays need Leave,
       * shifts need Shift Rostering). Such a step is not shown and does not count towards the total.
       */
      applicable: z.boolean(),
    }),
  ),
  /** Done steps among the applicable ones. */
  completed: z.number().int(),
  /** Applicable steps. */
  total: z.number().int(),
  preferences: setupPreferencesSchema,
  /**
   * True when the caller should be sent to the welcome setup: it has not been finished or skipped
   * AND the company is new (nobody but the owner works there yet). An established company that
   * predates the welcome setup never sees it.
   */
  showWelcome: z.boolean(),
  /**
   * The free-trial employee cap (`FREE_TRIAL_EMPLOYEE_LIMIT`): employees besides the owner's own
   * employment, how many exist, and how many more can be added. Null when the org is not capped
   * (a subscription that is not `trialing`, or no subscription and no unsigned signup trial).
   */
  employeeLimit: z
    .object({ limit: z.number().int(), used: z.number().int(), remaining: z.number().int() })
    .nullable(),
  /**
   * People with a current employment (not exited/rescinded, not archived), leaving out the org
   * owner — the same count as `employeeLimit.used`, so the two always agree. Present for every org,
   * capped or not; the web app uses it to keep nudging an owner who has not added a team yet.
   */
  teamSize: z.number().int().nonnegative(),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;

export const setupStatusContract = c.router(
  {
    getSetupStatus: {
      method: 'GET',
      path: '/setup-status',
      responses: { 200: setupStatusSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Which first-run setup steps this organisation has done (computed from its data)',
      metadata: routeMeta('org.settings:read'),
    },
    updateSetupPreferences: {
      method: 'PATCH',
      path: '/setup-preferences',
      body: updateSetupPreferencesBodySchema,
      responses: {
        200: setupPreferencesSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Record the first-run choices: welcome setup finished, areas to set up first, hide the Getting started panel',
      metadata: routeMeta('org.settings:update'),
    },
  },
  { pathPrefix: '/organisation' },
);
